import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, where, getDocs, Timestamp, addDoc, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar,
  Clock,
  Loader2,
  Users,
  Plus,
  X,
  Check,
  EyeOff,
  RefreshCw,
  Search
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { format, startOfDay, addMinutes, isSameDay, setHours, setMinutes } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

// Types
interface Coach {
  id: string;
  firstNameEn: string;
  lastNameEn: string;
  firstNameAr: string;
  lastNameAr: string;
  photoURL?: string;
}

interface Availability {
  id: string;
  coachId: string;
  startDate: Timestamp;
  endDate: Timestamp;
  selectedDays: { [key: string]: string[] };
  status: string;
}

interface Appointment {
  id: string;
  customerId: string;
  coachId: string;
  customerName?: string;
  customerNameEn?: string;
  customerNameAr?: string;
  customerPhone?: string;
  date: Date;
  status: string;
}

interface Customer {
  id: string;
  firstNameEn: string;
  lastNameEn: string;
  firstNameAr: string;
  lastNameAr: string;
  phone?: string;
}

// Session times (5 PM - 10 PM)
const SESSION_TIMES = {
  session1: { start: 17, end: 20 },
  session2: { start: 20, end: 22 }
};

// Generate time slots for display (5 PM to 10 PM in 15-min intervals)
const generateTimeSlots = () => {
  const slots: string[] = [];
  for (let hour = 17; hour < 22; hour++) {
    ['00', '15', '30', '45'].forEach(minutes => {
      slots.push(`${hour.toString().padStart(2, '0')}:${minutes}`);
    });
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

// Calendar Sidebar Component
function CalendarSidebar({ 
  currentDate, 
  onDateSelect, 
  isOpen, 
  onToggle,
  dir 
}: { 
  currentDate: Date; 
  onDateSelect: (date: Date) => void; 
  isOpen: boolean; 
  onToggle: () => void;
  dir: string;
}) {
  const months = dir === 'rtl' 
    ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  
  const days = dir === 'rtl'
    ? ['أح', 'إث', 'ثل', 'أر', 'خم', 'جم', 'سب']
    : ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
  
  const [displayMonth, setDisplayMonth] = useState(currentDate.getMonth());
  const [displayYear, setDisplayYear] = useState(currentDate.getFullYear());
  
  const daysInMonth = new Date(displayYear, displayMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(displayYear, displayMonth, 1).getDay();
  
  const calendarDays: (number | null)[] = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push(i);
  }
  
  const prevMonth = () => {
    if (displayMonth === 0) {
      setDisplayMonth(11);
      setDisplayYear(displayYear - 1);
    } else {
      setDisplayMonth(displayMonth - 1);
    }
  };
  
  const nextMonth = () => {
    if (displayMonth === 11) {
      setDisplayMonth(0);
      setDisplayYear(displayYear + 1);
    } else {
      setDisplayMonth(displayMonth + 1);
    }
  };
  
  const isSelectedDate = (day: number | null) => {
    if (!day) return false;
    return day === currentDate.getDate() && 
           displayMonth === currentDate.getMonth() && 
           displayYear === currentDate.getFullYear();
  };
  
  const isToday = (day: number | null) => {
    if (!day) return false;
    const today = new Date();
    return day === today.getDate() && 
           displayMonth === today.getMonth() && 
           displayYear === today.getFullYear();
  };
  
  const handleDateClick = (day: number | null) => {
    if (!day) return;
    const newDate = new Date(displayYear, displayMonth, day);
    onDateSelect(newDate);
  };
  
  const goToToday = () => {
    const today = new Date();
    setDisplayMonth(today.getMonth());
    setDisplayYear(today.getFullYear());
    onDateSelect(today);
  };

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/25 z-30 md:hidden"
          onClick={onToggle}
        />
      )}
      
      {/* Desktop sidebar */}
      <div className="hidden md:block w-52 border-r bg-white overflow-y-auto flex-shrink-0">
        <div className="p-4">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-3">
            <button className="p-1 hover:bg-gray-100 rounded" onClick={prevMonth}>
              <ChevronLeft size={16} />
            </button>
            <span className="font-medium text-sm">{months[displayMonth]} {displayYear}</span>
            <button className="p-1 hover:bg-gray-100 rounded" onClick={nextMonth}>
              <ChevronRight size={16} />
            </button>
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7 text-xs gap-1">
            {days.map(day => (
              <div key={day} className="text-center font-semibold text-muted-foreground py-1">{day}</div>
            ))}
            {calendarDays.map((day, i) => (
              <div
                key={i}
                className={`text-center py-1.5 cursor-pointer rounded text-sm ${
                  day ? 'hover:bg-blue-50' : ''
                } ${isSelectedDate(day) ? 'bg-primary text-primary-foreground font-bold' : ''} ${
                  isToday(day) && !isSelectedDate(day) ? 'bg-blue-100' : ''
                }`}
                onClick={() => handleDateClick(day)}
              >
                {day}
              </div>
            ))}
          </div>
          
          {/* Today button */}
          <Button 
            variant="outline"
            className="w-full mt-4"
            size="sm"
            onClick={goToToday}
          >
            {dir === 'rtl' ? 'اليوم' : 'Today'}
          </Button>
        </div>
      </div>
      
      {/* Mobile sidebar - slide in */}
      <div 
        className={`fixed inset-y-0 ${dir === 'rtl' ? 'right-0' : 'left-0'} z-40 w-64 bg-white shadow-lg transform transition-transform duration-200 md:hidden ${
          isOpen ? 'translate-x-0' : (dir === 'rtl' ? 'translate-x-full' : '-translate-x-full')
        }`}
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <span className="font-medium">{dir === 'rtl' ? 'التقويم' : 'Calendar'}</span>
            <button onClick={onToggle} className="p-1 hover:bg-gray-100 rounded">
              <X size={18} />
            </button>
          </div>
          
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-3">
            <button className="p-1 hover:bg-gray-100 rounded" onClick={prevMonth}>
              <ChevronLeft size={16} />
            </button>
            <span className="font-medium text-sm">{months[displayMonth]} {displayYear}</span>
            <button className="p-1 hover:bg-gray-100 rounded" onClick={nextMonth}>
              <ChevronRight size={16} />
            </button>
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7 text-xs gap-1">
            {days.map(day => (
              <div key={day} className="text-center font-semibold text-muted-foreground py-1">{day}</div>
            ))}
            {calendarDays.map((day, i) => (
              <div
                key={i}
                className={`text-center py-1.5 cursor-pointer rounded text-sm ${
                  day ? 'hover:bg-blue-50' : ''
                } ${isSelectedDate(day) ? 'bg-primary text-primary-foreground font-bold' : ''} ${
                  isToday(day) && !isSelectedDate(day) ? 'bg-blue-100' : ''
                }`}
                onClick={() => {
                  handleDateClick(day);
                  onToggle();
                }}
              >
                {day}
              </div>
            ))}
          </div>
          
          <Button 
            variant="outline"
            className="w-full mt-4"
            size="sm"
            onClick={() => {
              goToToday();
              onToggle();
            }}
          >
            {dir === 'rtl' ? 'اليوم' : 'Today'}
          </Button>
        </div>
      </div>
    </>
  );
}

// Time Slot Component
function TimeSlot({ 
  time, 
  appointment, 
  isAvailable,
  isSelected,
  isCurrentTime,
  onClick,
  language,
  dir
}: { 
  time: string; 
  appointment: Appointment | null;
  isAvailable: boolean;
  isSelected: boolean;
  isCurrentTime: boolean;
  onClick: () => void;
  language: string;
  dir: string;
}) {
  const formatTimeToAMPM = (timeString: string) => {
    const [hourStr, minuteStr] = timeString.split(':');
    const hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? (language === 'ar' ? 'م' : 'PM') : (language === 'ar' ? 'ص' : 'AM');
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minuteStr} ${ampm}`;
  };

  const getStatusColor = () => {
    if (appointment) {
      switch (appointment.status) {
        case 'completed':
          return 'bg-green-100 hover:bg-green-200 border-green-200';
        case 'cancelled':
          return 'bg-red-100 hover:bg-red-200 border-red-200';
        case 'missed':
          return 'bg-orange-100 hover:bg-orange-200 border-orange-200';
        default:
          return 'bg-blue-100 hover:bg-blue-200 border-blue-200';
      }
    }
    if (isSelected) return 'bg-primary/20 border-primary';
    if (!isAvailable) return 'bg-gray-200 border-gray-300 cursor-not-allowed';
    if (isCurrentTime) return 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100';
    return 'bg-white hover:bg-gray-50 border-gray-200';
  };

  const getCustomerName = () => {
    if (!appointment) return null;
    return language === 'ar' 
      ? appointment.customerNameAr || appointment.customerName || 'مستفيد'
      : appointment.customerNameEn || appointment.customerName || 'Customer';
  };

  return (
    <div 
      className={`p-1.5 text-xs border-b border-r transition-colors duration-150 cursor-pointer select-none h-[36px] flex items-center justify-between ${getStatusColor()}`}
      onClick={isAvailable || appointment ? onClick : undefined}
      title={appointment ? `${getCustomerName()} - ${appointment.status}` : formatTimeToAMPM(time)}
    >
      {appointment ? (
        <div className="flex-1 truncate">
          <div className="font-semibold truncate text-[11px]">{getCustomerName()}</div>
        </div>
      ) : (
        <span className="text-gray-400 text-[10px]">{formatTimeToAMPM(time)}</span>
      )}
      {appointment && (
        <Badge 
          variant={appointment.status === 'completed' ? 'default' : 'secondary'} 
          className="text-[9px] px-1 py-0 h-4 shrink-0"
        >
          {appointment.status === 'scheduled' ? (language === 'ar' ? 'مجدول' : 'Sched') :
           appointment.status === 'completed' ? (language === 'ar' ? 'مكتمل' : 'Done') :
           appointment.status === 'cancelled' ? (language === 'ar' ? 'ملغي' : 'Canc') :
           appointment.status}
        </Badge>
      )}
    </div>
  );
}

// Coach Column Component
function CoachColumn({
  coach,
  appointments,
  selectedDate,
  availabilities,
  selectedSlot,
  onSlotClick,
  language,
  dir
}: {
  coach: Coach;
  appointments: Appointment[];
  selectedDate: Date;
  availabilities: Availability[];
  selectedSlot: { time: string; coachId: string } | null;
  onSlotClick: (time: string, coachId: string, appointment?: Appointment) => void;
  language: string;
  dir: string;
}) {
  // Check if a time slot is within availability
  const isSlotAvailable = (time: string) => {
    const [hour, minute] = time.split(':').map(Number);
    const slotMinutes = hour * 60 + minute;
    const dayOfWeek = selectedDate.getDay().toString();
    
    for (const availability of availabilities) {
      const startDate = availability.startDate.toDate();
      const endDate = availability.endDate.toDate();
      
      // Check if selected date is within availability range
      if (selectedDate >= startDate && selectedDate <= endDate) {
        const selectedSessions = availability.selectedDays?.[dayOfWeek] || [];
        
        for (const session of selectedSessions) {
          const sessionTimes = SESSION_TIMES[session as keyof typeof SESSION_TIMES];
          if (!sessionTimes) continue;
          
          const sessionStart = sessionTimes.start * 60;
          const sessionEnd = sessionTimes.end * 60;
          
          if (slotMinutes >= sessionStart && slotMinutes < sessionEnd) {
            return true;
          }
        }
      }
    }
    
    return false;
  };

  // Get appointment for a specific time slot
  const getAppointmentForSlot = (time: string) => {
    const [hour, minute] = time.split(':').map(Number);
    
    return appointments.find(apt => {
      const aptDate = apt.date instanceof Date ? apt.date : new Date(apt.date);
      return aptDate.getHours() === hour && 
             aptDate.getMinutes() === minute &&
             isSameDay(aptDate, selectedDate);
    });
  };

  // Get current time slot
  const getCurrentTimeSlot = () => {
    const now = new Date();
    if (!isSameDay(now, selectedDate)) return '';
    const hour = now.getHours().toString().padStart(2, '0');
    const minute = (Math.floor(now.getMinutes() / 15) * 15).toString().padStart(2, '0');
    return `${hour}:${minute}`;
  };

  const currentTimeSlot = getCurrentTimeSlot();

  return (
    <div className="min-w-[140px] flex-1">
      {TIME_SLOTS.map((time) => {
        const appointment = getAppointmentForSlot(time);
        const isAvailable = isSlotAvailable(time);
        const isSelected = selectedSlot?.time === time && selectedSlot?.coachId === coach.id;
        
        return (
          <TimeSlot
            key={time}
            time={time}
            appointment={appointment || null}
            isAvailable={isAvailable}
            isSelected={isSelected}
            isCurrentTime={time === currentTimeSlot}
            onClick={() => onSlotClick(time, coach.id, appointment)}
            language={language}
            dir={dir}
          />
        );
      })}
    </div>
  );
}

// Main Scheduler Component
export function AppointmentsScheduler() {
  const { t, dir, language } = useLanguage();
  const dateLocale = language === 'ar' ? ar : enUS;
  
  // State
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [availabilities, setAvailabilities] = useState<Availability[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [hideEmpty, setHideEmpty] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Appointment creation state
  const [selectedSlot, setSelectedSlot] = useState<{ time: string; coachId: string } | null>(null);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Appointment details state
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);

  // Load coaches with approved availabilities
  const loadCoachesWithAvailability = useCallback(async () => {
    try {
      // Get all approved availabilities
      const availabilitiesQuery = query(
        collection(db, 'availabilities'),
        where('status', '==', 'approved')
      );
      const availabilitiesSnapshot = await getDocs(availabilitiesQuery);
      const availabilitiesData = availabilitiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Availability[];
      
      setAvailabilities(availabilitiesData);
      
      // Get unique coach IDs from availabilities
      const coachIds = [...new Set(availabilitiesData.map(a => a.coachId))];
      
      if (coachIds.length === 0) {
        setCoaches([]);
        return;
      }
      
      // Get coach details
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      const coachesData = usersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as Coach))
        .filter(user => coachIds.includes(user.id));
      
      setCoaches(coachesData);
    } catch (error) {
      console.error('Error loading coaches:', error);
      toast.error(language === 'ar' ? 'فشل في تحميل الأطباء' : 'Failed to load doctors');
    }
  }, [language]);

  // Load customers
  const loadCustomers = useCallback(async () => {
    try {
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      const customersData = usersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter((user: any) => user.roles?.some((r: any) => r.role === 'customer')) as Customer[];
      
      setCustomers(customersData);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, []);

  // Load appointments for selected date
  const loadAppointments = useCallback(async () => {
    try {
      const startOfSelectedDay = startOfDay(selectedDate);
      const endOfSelectedDay = new Date(startOfSelectedDay);
      endOfSelectedDay.setHours(23, 59, 59, 999);
      
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('date', '>=', Timestamp.fromDate(startOfSelectedDay)),
        where('date', '<=', Timestamp.fromDate(endOfSelectedDay))
      );
      
      const unsubscribe = onSnapshot(appointmentsQuery, async (snapshot) => {
        const appointmentsData = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              ...data,
              date: data.date.toDate()
            } as Appointment;
          })
        );
        
        setAppointments(appointmentsData);
        setLoading(false);
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('Error loading appointments:', error);
      setLoading(false);
    }
  }, [selectedDate]);

  // Initial load
  useEffect(() => {
    setLoading(true);
    Promise.all([
      loadCoachesWithAvailability(),
      loadCustomers()
    ]).then(() => {
      loadAppointments();
    });
  }, [loadCoachesWithAvailability, loadCustomers]);

  // Reload appointments when date changes
  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Filter coaches
  const getFilteredCoaches = () => {
    let filtered = [...coaches];
    
    // Hide empty (coaches with no appointments on selected day)
    if (hideEmpty) {
      filtered = filtered.filter(coach => 
        appointments.some(apt => apt.coachId === coach.id)
      );
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(coach => {
        const nameEn = `${coach.firstNameEn} ${coach.lastNameEn}`.toLowerCase();
        const nameAr = `${coach.firstNameAr} ${coach.lastNameAr}`;
        return nameEn.includes(query) || nameAr.includes(query);
      });
    }
    
    return filtered;
  };

  // Handle slot click
  const handleSlotClick = (time: string, coachId: string, appointment?: Appointment) => {
    if (appointment) {
      setSelectedAppointment(appointment);
      setShowAppointmentDetails(true);
    } else {
      setSelectedSlot({ time, coachId });
      setShowAppointmentDialog(true);
    }
  };

  // Create appointment
  const handleCreateAppointment = async () => {
    if (!selectedSlot || !selectedCustomerId) {
      toast.error(language === 'ar' ? 'اختر مستفيد' : 'Please select a customer');
      return;
    }
    
    try {
      setIsSaving(true);
      
      const [hour, minute] = selectedSlot.time.split(':').map(Number);
      const appointmentDate = new Date(selectedDate);
      appointmentDate.setHours(hour, minute, 0, 0);
      
      const customer = customers.find(c => c.id === selectedCustomerId);
      
      await addDoc(collection(db, 'appointments'), {
        customerId: selectedCustomerId,
        coachId: selectedSlot.coachId,
        customerName: customer ? `${customer.firstNameEn} ${customer.lastNameEn}`.trim() : '',
        customerNameEn: customer ? `${customer.firstNameEn} ${customer.lastNameEn}`.trim() : '',
        customerNameAr: customer ? `${customer.firstNameAr} ${customer.lastNameAr}`.trim() : '',
        customerPhone: customer?.phone || '',
        date: Timestamp.fromDate(appointmentDate),
        status: 'scheduled',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      
      toast.success(language === 'ar' ? 'تم إنشاء الموعد بنجاح' : 'Appointment created successfully');
      setShowAppointmentDialog(false);
      setSelectedSlot(null);
      setSelectedCustomerId('');
      setCustomerSearch('');
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast.error(language === 'ar' ? 'فشل في إنشاء الموعد' : 'Failed to create appointment');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete appointment
  const handleDeleteAppointment = async () => {
    if (!selectedAppointment) return;
    
    try {
      await deleteDoc(doc(db, 'appointments', selectedAppointment.id));
      toast.success(language === 'ar' ? 'تم حذف الموعد' : 'Appointment deleted');
      setShowAppointmentDetails(false);
      setSelectedAppointment(null);
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error(language === 'ar' ? 'فشل في حذف الموعد' : 'Failed to delete appointment');
    }
  };

  // Filter customers for search
  const filteredCustomers = customerSearch.trim() 
    ? customers.filter(c => {
        const nameEn = `${c.firstNameEn} ${c.lastNameEn}`.toLowerCase();
        const nameAr = `${c.firstNameAr} ${c.lastNameAr}`;
        const phone = c.phone || '';
        return nameEn.includes(customerSearch.toLowerCase()) || 
               nameAr.includes(customerSearch) ||
               phone.includes(customerSearch);
      }).slice(0, 10)
    : [];

  // Get coach name
  const getCoachName = (coachId: string) => {
    const coach = coaches.find(c => c.id === coachId);
    if (!coach) return language === 'ar' ? 'طبيب' : 'Doctor';
    return language === 'ar' 
      ? `${coach.firstNameAr} ${coach.lastNameAr}`.trim() || `${coach.firstNameEn} ${coach.lastNameEn}`.trim()
      : `${coach.firstNameEn} ${coach.lastNameEn}`.trim();
  };

  const filteredCoaches = getFilteredCoaches();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" dir={dir}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold">{language === 'ar' ? 'جدولة المواعيد' : 'Appointments Scheduler'}</h3>
          <p className="text-sm text-muted-foreground">
            {format(selectedDate, 'EEEE, d MMMM yyyy', { locale: dateLocale })}
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button 
            variant="outline" 
            size="sm"
            className="md:hidden"
            onClick={() => setIsSidebarOpen(true)}
          >
            <Calendar className="h-4 w-4" />
          </Button>
          <Button 
            variant={hideEmpty ? "default" : "outline"} 
            size="sm"
            onClick={() => setHideEmpty(!hideEmpty)}
          >
            <EyeOff className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
            {language === 'ar' ? 'إخفاء الفارغة' : 'Hide Empty'}
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              setLoading(true);
              loadCoachesWithAvailability().then(() => loadAppointments());
            }}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className={`absolute top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground ${dir === 'rtl' ? 'right-3' : 'left-3'}`} />
        <Input
          placeholder={language === 'ar' ? 'بحث عن طبيب...' : 'Search doctor...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={dir === 'rtl' ? 'pr-10' : 'pl-10'}
        />
      </div>

      {/* Main Content */}
      <div className="flex border rounded-lg overflow-hidden bg-white" style={{ height: 'calc(100vh - 350px)', minHeight: '400px' }}>
        {/* Calendar Sidebar */}
        <CalendarSidebar 
          currentDate={selectedDate}
          onDateSelect={setSelectedDate}
          isOpen={isSidebarOpen}
          onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
          dir={dir}
        />

        {/* Schedule Grid */}
        <div className="flex-1 overflow-auto">
          {filteredCoaches.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Users className="h-12 w-12 mb-4 opacity-50" />
              <p>{language === 'ar' ? 'لا يوجد أطباء متاحين' : 'No doctors available'}</p>
              <p className="text-sm">{language === 'ar' ? 'تأكد من وجود جداول معتمدة' : 'Make sure there are approved schedules'}</p>
            </div>
          ) : (
            <div className="flex min-w-max">
              {/* Time Column */}
              <div className="w-16 flex-shrink-0 sticky left-0 z-10 bg-gray-50 border-r">
                {/* Header */}
                <div className="h-12 border-b flex items-center justify-center">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </div>
                {/* Time labels */}
                {TIME_SLOTS.map((time) => {
                  const [hour] = time.split(':');
                  const isHourStart = time.endsWith(':00');
                  return (
                    <div 
                      key={time} 
                      className={`h-[36px] border-b flex items-center justify-center text-xs text-muted-foreground ${isHourStart ? 'font-medium' : ''}`}
                    >
                      {isHourStart && (
                        <span>
                          {parseInt(hour) > 12 ? parseInt(hour) - 12 : hour}:00 {parseInt(hour) >= 12 ? (language === 'ar' ? 'م' : 'PM') : (language === 'ar' ? 'ص' : 'AM')}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Doctor Columns */}
              {filteredCoaches.map((coach) => (
                <div key={coach.id} className="min-w-[140px] flex-1 border-r last:border-r-0">
                  {/* Header */}
                  <div className="h-12 border-b bg-gray-50 p-2 flex items-center">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-semibold text-sm mr-2 shrink-0">
                      {(language === 'ar' ? coach.firstNameAr?.[0] : coach.firstNameEn?.[0]) || 'D'}
                    </div>
                    <span className="text-sm font-medium truncate">
                      {language === 'ar' 
                        ? `${coach.firstNameAr || ''} ${coach.lastNameAr || ''}`.trim() || `${coach.firstNameEn || ''} ${coach.lastNameEn || ''}`.trim()
                        : `${coach.firstNameEn || ''} ${coach.lastNameEn || ''}`.trim()
                      }
                    </span>
                  </div>
                  {/* Slots */}
                  <CoachColumn
                    coach={coach}
                    appointments={appointments.filter(a => a.coachId === coach.id)}
                    selectedDate={selectedDate}
                    availabilities={availabilities.filter(a => a.coachId === coach.id)}
                    selectedSlot={selectedSlot}
                    onSlotClick={handleSlotClick}
                    language={language}
                    dir={dir}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Appointment Dialog */}
      <Dialog open={showAppointmentDialog} onOpenChange={setShowAppointmentDialog}>
        <DialogContent className="sm:max-w-md" dir={dir}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              {language === 'ar' ? 'موعد جديد' : 'New Appointment'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Slot info */}
            {selectedSlot && (
              <div className="p-3 bg-muted rounded-lg space-y-1">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4" />
                  <span>{format(selectedDate, 'EEEE, d MMMM yyyy', { locale: dateLocale })}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4" />
                  <span>{selectedSlot.time}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4" />
                  <span>{getCoachName(selectedSlot.coachId)}</span>
                </div>
              </div>
            )}
            
            {/* Customer search */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'المستفيد' : 'Customer'}</Label>
              <Input
                placeholder={language === 'ar' ? 'بحث بالاسم أو الهاتف...' : 'Search by name or phone...'}
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
              />
              
              {filteredCustomers.length > 0 && (
                <div className="max-h-40 overflow-y-auto border rounded-md">
                  {filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      className={`p-2 hover:bg-muted cursor-pointer flex items-center justify-between ${
                        selectedCustomerId === customer.id ? 'bg-primary/10' : ''
                      }`}
                      onClick={() => {
                        setSelectedCustomerId(customer.id);
                        setCustomerSearch(
                          language === 'ar' 
                            ? `${customer.firstNameAr} ${customer.lastNameAr}`.trim()
                            : `${customer.firstNameEn} ${customer.lastNameEn}`.trim()
                        );
                      }}
                    >
                      <div>
                        <div className="text-sm font-medium">
                          {language === 'ar' 
                            ? `${customer.firstNameAr} ${customer.lastNameAr}`.trim()
                            : `${customer.firstNameEn} ${customer.lastNameEn}`.trim()
                          }
                        </div>
                        {customer.phone && (
                          <div className="text-xs text-muted-foreground">{customer.phone}</div>
                        )}
                      </div>
                      {selectedCustomerId === customer.id && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAppointmentDialog(false)}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleCreateAppointment} disabled={!selectedCustomerId || isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                  {language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
                </>
              ) : (
                <>
                  <Check className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                  {language === 'ar' ? 'إنشاء الموعد' : 'Create Appointment'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Appointment Details Dialog */}
      <Dialog open={showAppointmentDetails} onOpenChange={setShowAppointmentDetails}>
        <DialogContent className="sm:max-w-md" dir={dir}>
          <DialogHeader>
            <DialogTitle>{language === 'ar' ? 'تفاصيل الموعد' : 'Appointment Details'}</DialogTitle>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">
                    {language === 'ar' 
                      ? selectedAppointment.customerNameAr || selectedAppointment.customerName 
                      : selectedAppointment.customerNameEn || selectedAppointment.customerName
                    }
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>{format(selectedAppointment.date, 'PPP p', { locale: dateLocale })}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={selectedAppointment.status === 'completed' ? 'default' : 'secondary'}>
                    {selectedAppointment.status}
                  </Badge>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAppointmentDetails(false)}>
              {language === 'ar' ? 'إغلاق' : 'Close'}
            </Button>
            <Button variant="destructive" onClick={handleDeleteAppointment}>
              {language === 'ar' ? 'حذف الموعد' : 'Delete Appointment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
